<template>
    <Head title="Create Review Post"/>
    <BreezeAuthenticatedLayout>
        <template #header>
            <h2 class="font-semibold text-xl leading-tight">
                Create Review Post
            </h2>
        </template>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="w-full my-6 overflow-hidden py-12 shadow">
            <div class="px-6 pt-4 pb-2">
                <form enctype="multipart/form-data"
                      @submit.prevent="create.post(requestUrl, { preserveScroll: true})"
                      class="w-full">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Title</admin-app-label>
                            <input-basic
                                placeholder="Title"
                                v-model="create.title"
                                :error="errors['title']"
                            />
                            <input-error v-if="errors['title']">
                                {{ errors.title }}
                            </input-error>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Slug</admin-app-label>
                            <input-basic
                                placeholder="Slug"
                                v-model="create.slug"
                                :error="errors['slug']"
                            />
                            <input-error v-if="errors['slug']">
                                {{ errors.slug }}
                            </input-error>
                            <p class="text-sm text-placeholder">
                                Please note the slug cannot be edited once set. Leave empty to use an auto generated
                                slug
                                from the title.
                            </p>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Posted By</admin-app-label>
                            <input-select
                                placeholder="Posted By"
                                labelKey="Product"
                                v-model="create.created_by"
                                :options="users"
                                :error="errors['created_by']"
                                label="name"
                                :reduce="users => users.id"
                            >
                            </input-select>
                            <p class="text-sm text-placeholder">
                                Non compulsory field. Set this to show who created the post
                            </p>
                        </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Excerpt</admin-app-label>
                            <input-textarea
                                class="min-h-[200px]"
                                placeholder="Excerpt"
                                v-model="create.excerpt"
                                :error="errors['excerpt']"
                            />
                            <input-error v-if="errors['excerpt']">
                                {{ errors.excerpt }}
                            </input-error>
                            <p class="text-sm text-placeholder mt-1">
                                Add a small excerpt that will serve as a short description of this news post.
                                This excerpt will be used on the news landing page for a short description of the
                                article.
                            </p>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Video ID</admin-app-label>
                            <input-basic
                                placeholder="Video ID"
                                v-model="create.video_id"
                                :error="errors[`video_id`]"
                            />
                            <p class="text-sm">e.g youtube.com/watch?v=<strong>5eZOOEkrf6Q</strong></p>
                        </div>
                    </div>

                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Content</admin-app-label>
                        <input-wysiwyg
                            v-model="create.content"
                        ></input-wysiwyg>
                        <input-error v-if="errors['content']">
                            {{ errors.description }}
                        </input-error>
                    </div>

                    <div class="mb-6 md:mb-4">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <admin-app-label>CTA Text</admin-app-label>
                                <input-basic
                                    placeholder="CTA Text"
                                    v-model="create.cta_text"
                                    :error="errors['cta_text']"
                                />
                                <input-error v-if="errors['cta_text']">
                                    {{ errors.cta_text }}
                                </input-error>
                            </div>

                            <div>
                                <admin-app-label>CTA Link</admin-app-label>
                                <input-basic
                                    placeholder="CTA Link"
                                    v-model="create.cta_link"
                                    :error="errors['cta_link']"
                                />
                                <input-error v-if="errors['cta_link']">
                                    {{ errors.cta_link }}
                                </input-error>
                            </div>
                        </div>

                        <p class="text-sm text-placeholder mt-1">
                            Add a call to action button to the bottom of this article.
                            You can link to an external site, or another page within {{ config.name }}.
                        </p>
                    </div>

                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Featured Brand</admin-app-label>
                        <input-select
                            placeholder="Featured Brand"
                            labelKey="Featured Brand"
                            v-model="create.featured_brand_id"
                            :options="brands"
                            :error="errors['featured_brand_id']"
                            label="name"
                            :reduce="brands => brands.id"
                        />
                        <p class="text-sm text-placeholder mt-1">Leave this blank to default to Bridgestone</p>
                    </div>

                    <seo-tags v-model="create.seo_tags" :errors="errors"></seo-tags>
                    <app-button type="submit" :disabled="create.processing" class="mt-8">Create</app-button>
                </form>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
<script>
import {Head, useForm} from "@inertiajs/inertia-vue3";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated.vue";
import Label from "@/Components/Label";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import AppButton from "@/Components/AppButton";
import InputError from "@/Components/Forms/Inputs/InputError";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";
import ImageUpload from "@/Components/Admin/Forms/Inputs/ImageUpload";
import InputWysiwyg from "@/Components/Admin/Forms/Inputs/InputWysiwyg";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import AddVideos from "@/Components/Admin/Forms/AddVideos";
import ProductMultiSelect from "@/Components/Admin/Forms/ProductSelection";
import InputTextarea from "@/Components/Admin/Forms/Inputs/InputTextarea";
import SeoTags from "@/Components/Admin/Forms/SeoTags";

export default {
    name: "Create",
    components: {
        SeoTags,
        ProductMultiSelect,
        AddVideos,
        BreadCrumbs,
        InputWysiwyg,
        ImageUpload,
        InputSelect,
        AdminAppLabel,
        AppButton,
        InputBasic,
        Label,
        BreezeAuthenticatedLayout,
        Head,
        InputError,
        InputTextarea
    },
    props: {
        users: Object,
        brands: Object,
        errors: Object,
        config: Object,
        requestUrl: {
            type: String,
            default: "/admin/review-posts"
        }
    },
    data() {
        return {
            navItems: [
                {title: "Review Posts", path: "/admin/review-posts"},
                {title: "Create New", path: "/admin/review-posts/create"},
            ],
            create: useForm(
                {
                    title: "",
                    slug: "",
                    created_by: "",
                    excerpt: "",
                    content: "",
                    video_id: "",
                    cta_link: "",
                    cta_text: "",
                    featured_brand_id: "",
                    posted_date: "",
                    seo_tags: null
                }
            )
        };
    }
};
</script>
